// MessageManager と Message の相互参照を解消すれば import/no-cycle の rule を disable しなくて済むが…
// ref. https://github.com/benmosher/eslint-plugin-import/blob/master/docs/rules/no-cycle.md
/* eslint-disable import/no-cycle */
import { callPostback, fetchMessage } from '../api-clients';
import { fetchRecommendations } from '../api-clients/silveregg';
import {
  DEFAULT_CLOSE_MESSAGES_ON_PAGECHANGE,
  SILVER_EGG_UID_STORAGE_KEY,
  STANDARD_EVENT_NAME,
  USER_ID_STORAGE_KEY,
} from '../constants';
import { ErrorNotifier } from '../error-notifier';
import { EventChunkUploader } from '../event-chunk-uploader';
import { EventLog } from '../event-log';
import { MessageManager } from '../message-manager';
import { ActionMeasurement, PrebuildMessage } from '../message.types';
import { Recommendation, SilverEggSetting } from '../recommendation.types';
import { TriggeredEvent } from '../triggered-event.types';
import { storage } from '../utility/storage';
import { EmbeddedMessageDOM } from './dom/embedded-message-dom';
import { FixedMessageDOM } from './dom/fixed-message-dom';
import { RelativeMessageDOM } from './dom/relative-message-dom';
import { MessagePosition } from './message-position.types';
import { MessageProxyV0 } from './message-proxy-v0';
import { MessageProxyV1 } from './message-proxy-v1';
import { MessageTrigger, MessageTriggerBuilder } from './message-trigger';
import { TriggerTypes } from './message-trigger.types';

type MessageFromConfiguration = PrebuildMessage;
type SdkToken = string;
type ActionButtonCallback = (message: Message, action: string, eventName?: string) => void;
type MessageShowCallback = (messge: Message, action: 'show', eventName?: never) => void;
type MessageCloseCallback = (messge: Message, action: 'close', eventName?: never) => void;
type PagechangeCallback = (message: Message, action: 'pagechange', eventName?: never) => void;
type MessageCallback = ActionButtonCallback & MessageShowCallback & MessageCloseCallback;

export interface MessageParams {
  message: MessageFromConfiguration;
  manager: MessageManager;
  token: SdkToken;
  idfv: string;
  eventChunkUploader: EventChunkUploader;
  eventLog: EventLog;
  isControlGroup: boolean;
  silverEggSetting: SilverEggSetting;
  onActionButtonClick?: ActionButtonCallback;
  onMessageShow?: MessageShowCallback;
  onMessageClose?: MessageCloseCallback;
  onPagechange?: PagechangeCallback;
}

const TRIGGER_PRIORITY_MID = 2;

let PRELOADED_CACHE: { [imageUrl: string]: boolean } = {};
export const clearPreloadedCache = () => {
  PRELOADED_CACHE = {};
};
export class Message {
  private manager: MessageManager;
  protected dom: FixedMessageDOM | EmbeddedMessageDOM | RelativeMessageDOM;
  public isCurrentlyVisible: boolean;
  public messageId: string;
  public variantGroupId: string;
  public trigger: MessageTrigger;
  public isSimultaneouslyDisplayable: boolean;
  public triggerPriority: number;
  public updatedAt: number;
  public showEvenIfAlreadyRead: boolean;
  public totalImpressionLimit: number | undefined;
  public periodicImpressionLimit: number | undefined;
  public totalImpressionCount: number | undefined;
  public periodicImpressionCount: number | undefined;

  public isControlGroup: boolean;
  public isV2Template: boolean;
  public showCount = 0;
  public closeOnPagechange: boolean;
  private silverEggSetting: {
    domain: string;
    merchantId: string;
  };

  protected position: MessagePosition;

  // TODO: 現在は panel.view_html/view_css としてレンダリングされた完全な HTML/CSS 文字列をサーバーサイドからもらう形になっている。
  // このため、 body や header など個別のプロパティを Message インスタンスで保持しておく必要は基本的にないものの、
  // imageUrl など一部のプロパティについては依存する処理が存在するため、一旦は残しておく。
  protected closeButtonClickTrigger: string;
  public imageUrl: string;
  protected script: string = '';
  protected shouldExecuteScript: boolean = false;
  protected scopedCss: boolean = true;
  protected metadata: Record<string, any>;
  protected measurementParams: ActionMeasurement[];

  protected token: SdkToken; // TODO: SocialProofMessage を統合したら private に変更
  private idfv: string;
  private eventChunkUploader: EventChunkUploader;

  protected callbacks: {
    onActionButtonClick: ActionButtonCallback[];
    onMessageShow: MessageShowCallback[];
    onMessageClose: MessageCloseCallback[];
    onPagechange: PagechangeCallback[];
  };

  public constructor({
    message,
    manager,
    token,
    idfv,
    eventChunkUploader,
    eventLog,
    isControlGroup,
    silverEggSetting,
    onMessageShow,
    onActionButtonClick,
    onMessageClose,
    onPagechange,
  }: MessageParams) {
    this.manager = manager;
    this.isCurrentlyVisible = false;
    this.metadata = message.metadata;
    this.messageId = message.id;
    this.variantGroupId = message.variant_group_id;
    this.isSimultaneouslyDisplayable = message.simultaneous_display || false;
    this.triggerPriority = message.trigger_priority || TRIGGER_PRIORITY_MID;
    this.updatedAt = message.updated_at || 0;
    this.showEvenIfAlreadyRead = message.show_even_if_already_read || false;
    this.totalImpressionLimit = message.total_impression_limit;
    this.periodicImpressionLimit = message.periodic_impression_limit;
    this.totalImpressionCount = message.total_impression_count;
    this.periodicImpressionCount = message.periodic_impression_count;

    const panel = message.panels?.[0];
    this.position = message.position ?? { type: 'fixed', placement: 'center' };
    this.shouldExecuteScript = message.use_script;
    this.isControlGroup = isControlGroup;
    this.isV2Template = !!panel?.is_v2_template;
    this.closeOnPagechange = message.close_on_pagechange ?? DEFAULT_CLOSE_MESSAGES_ON_PAGECHANGE;
    this.silverEggSetting = silverEggSetting;

    this.scopedCss = !!panel?.scoped_css;
    this.closeButtonClickTrigger = panel?.close_button_click_trigger ?? '';
    this.imageUrl = panel?.image_url ?? '';
    this.script = panel?.view_javascript || '';
    this.measurementParams = panel?.measurements || [];

    this.token = token;
    this.idfv = idfv;
    this.eventChunkUploader = eventChunkUploader;
    this.callbacks = {
      onActionButtonClick: onActionButtonClick ? [onActionButtonClick] : [],
      onMessageShow: onMessageShow ? [onMessageShow] : [],
      onMessageClose: onMessageClose ? [onMessageClose] : [],
      onPagechange: onPagechange ? [onPagechange] : [],
    };

    // v2.15.x 以降で非プレビュー時においては view_html および view_css のプロパティが
    // panel に含まれなくなるので、SDK 側でケアをする
    const { view_html: rawHtml = '', view_css: rawCss = '' } = panel ?? { view_html: '', view_css: '' };
    this.dom = this.buildDomWithCurProps({ rawHtml, rawCss });

    this.trigger = MessageTriggerBuilder.build(
      message.complex_trigger.type as TriggerTypes,
      message.complex_trigger.condition,
      eventLog
    );
  }

  // TODO: MessageManager への移動も検討
  public isConditionSatisfied(event: TriggeredEvent): Promise<boolean> {
    return this.trigger.isConditionSatisfied(event);
  }

  // TODO: imageUrl など、個別のプロパティに依存せずに BaseMessageDOM#el をパースして画像をロードするようにする
  // またその際は処理を BaseMessageDOM 側に移すことも検討する
  // ref. https://github.com/reproio/web-sdk/pull/374#discussion_r293175104
  public preloadImage(): void | false {
    if (!this.imageUrl) return false;
    if (PRELOADED_CACHE[this.imageUrl]) return;
    PRELOADED_CACHE[this.imageUrl] = true;
    new Image().src = this.imageUrl;
  }

  public get isDOMReady(): boolean {
    return this.dom.isReady;
  }

  public async prepareShowProcess(_params = {}): Promise<boolean> {
    try {
      const {
        closeButtonClickTrigger,
        measurements,
        useThirdPartyService,
        specId,
        itemNum,
        rawHtml,
        rawCss,
        rawJavaScript,
        metadata,
      } = await this.fetchMessageFromApi();

      let recommendation: Recommendation | undefined;
      if (useThirdPartyService) {
        recommendation = await this.fetchRecommendation({ specId, itemNum });
      }

      this.script = rawJavaScript;
      this.closeButtonClickTrigger = closeButtonClickTrigger;
      this.measurementParams = measurements;
      if (this.dom) this.dom.close();
      this.dom = this.buildDomWithCurProps({ rawHtml, rawCss, recommendation });

      if (useThirdPartyService) {
        this.attachRecommendedMessageEventListener();
      }

      if (metadata) {
        this.metadata = { ...this.metadata, ...metadata };
      }

      return true;
    } catch (error) {
      this.isCurrentlyVisible = false;
      throw error;
    }
  }

  public async startShowProcess(): Promise<void> {
    if (this.shouldExecuteScript) {
      try {
        this.executeScript();
      } catch (error) {
        ErrorNotifier.setContext('Custom JS Error');
        ErrorNotifier.notify({ error: error as Error, extraPayload: {}, samplingRatio: 1000 });
        ErrorNotifier.clearContext();
        throw error;
      }
      return;
    }
    this.show();
  }

  public show(): void {
    if (!this.isSimultaneouslyDisplayable) {
      if (!this.manager.canShowNonSimultaneouslyDisplayableMessage) return;
      this.denyShowNonSimultaneouslyDisplayableMessage();
    }

    // TODO: jest でのテスト実施時にのみ UnhandledPromiseRejectionWarning が表示されて鬱陶しいので、どうにかする
    if (this.isCurrentlyVisible) return;
    this.isCurrentlyVisible = true;

    this.showCount += 1;
    this.injectDom();
    this.callbacks.onMessageShow.forEach((f) => f?.(this, 'show'));
    this.uploadImpressionEvent();
  }

  protected executeScript(): void {
    // Message オブジェクトに存在する全てのメソッドやプロパティを触られると困るので
    // Message.script から実行されるスクリプトは MessageProxy を通してしか Message の操作をできないようにする
    const func = new Function('message', 'messageV1', this.script); // eslint-disable-line no-new-func
    func(
      new MessageProxyV0(this, this.dom),
      () => new MessageProxyV1(this, this.dom) // V1 からは直接のインスタンスではなく、ジェネレータを渡す
    );
  }

  public addCallback<K extends keyof typeof this.callbacks>(event: K, callback: MessageCallback) {
    if (Object.keys(this.callbacks).includes(event)) {
      this.callbacks[event].push(callback);
    } else {
      console.error(`Unknown callback event: ${event}`);
    }
  }

  protected injectDom() {
    this.dom.show();
    this.dom.attachCloseButtonEventListener(() => this.clickClose());
    this.measurementParams.forEach((measurement) => {
      this.dom.attachCtaButtonEventListener({
        dataAttribute: measurement.data_attribute_name,
        onButtonClick: () => this.clickButtonCta(measurement.click_trigger, measurement.event_name ?? undefined),
      });
    });
  }

  public close(): void {
    const closed = this.dom.close();

    if (closed) {
      this.resetMessageVisibilityConditions();
      this.callbacks.onMessageClose.forEach((f) => f?.(this, 'close'));
    }
  }

  protected resetMessageVisibilityConditions() {
    this.isCurrentlyVisible = false;
    if (!this.isSimultaneouslyDisplayable) {
      this.allowShowNonSimultaneouslyDisplayableMessage();
    }
  }

  public clickClose(): void {
    if (!this.isCurrentlyVisible) return;

    this.close();
    this.eventChunkUploader.addEvent({
      name: STANDARD_EVENT_NAME('click_web_message_button'),
      tracked_at: new Date(),
      metadata: this.metadata,
      properties: {
        clicked_button_id: this.closeButtonClickTrigger,
      },
    });
  }

  public clickButtonCta(clickTrigger: string, eventName?: string): void {
    this.close();
    this.eventChunkUploader.addEvent({
      name: STANDARD_EVENT_NAME('click_web_message_button'),
      tracked_at: new Date(),
      metadata: this.metadata,
      properties: {
        clicked_button_id: clickTrigger,
      },
    });
    this.callbacks.onActionButtonClick.forEach((f) => f?.(this, clickTrigger, eventName));
  }

  protected uploadImpressionEvent(): void {
    this.eventChunkUploader.addEvent({
      name: STANDARD_EVENT_NAME('show_web_message'),
      tracked_at: new Date(),
      metadata: this.metadata,
      properties: {},
    });
  }

  private buildDomWithCurProps({
    rawHtml,
    rawCss,
    recommendation,
  }: {
    rawHtml: string;
    rawCss: string;
    recommendation?: Recommendation;
  }) {
    try {
      return this.buildDom({
        rawHtml,
        rawCss,
        scopedCss: this.scopedCss,
        scopedCssId: this.variantGroupId,
        position: this.position,
        shouldSanitizeHTML: !this.shouldExecuteScript,
        shouldRespectTargetInHTML: this.isV2Template,
        recommendation,
        elementShouldBeEmptyAndInvisible: this.isControlGroup,
      });
    } catch (e: any) {
      if (e.message.indexOf('buildDom') >= 0) {
        // Append additional information if error is `this.buildDom is not a function` https://rollbar.com/repro/web-sdk-production/items/158/
        e.message = `${e.message} (${JSON.stringify({
          type: typeof this.buildDom,
          contents: typeof this.buildDom === 'function' ? this.buildDom.toString() : JSON.stringify(this.buildDom),
        })})`;
      }
      throw e;
    }
  }

  protected buildDom(params: {
    rawHtml: string;
    rawCss: string;
    scopedCss: boolean;
    scopedCssId: string;
    position: MessagePosition;
    shouldSanitizeHTML: boolean;
    shouldRespectTargetInHTML: boolean;
    recommendation?: Recommendation;
    elementShouldBeEmptyAndInvisible: boolean;
  }): FixedMessageDOM | EmbeddedMessageDOM | RelativeMessageDOM {
    switch (params.position.type) {
      case 'embedded':
        return new EmbeddedMessageDOM(params, this.close.bind(this));
      case 'relative':
        return new RelativeMessageDOM(params, this.close.bind(this));
      case 'fixed':
        return new FixedMessageDOM(params, this.close.bind(this));
      default:
        // ${params.position.type} だとコンパイル通らない
        throw new Error(`unknown position type in params.position: ${JSON.stringify(params.position)}`);
    }
  }

  private allowShowNonSimultaneouslyDisplayableMessage(): void {
    this.manager.canShowNonSimultaneouslyDisplayableMessage = true;
  }

  protected denyShowNonSimultaneouslyDisplayableMessage(): void {
    this.manager.canShowNonSimultaneouslyDisplayableMessage = false;
  }

  public handlePagechange() {
    this.callbacks.onPagechange.forEach((f) => f?.(this, 'pagechange'));
    if (this.closeOnPagechange && this.isCurrentlyVisible) this.close();
  }

  /* ここから下の処理が Message API を叩いてメッセージを表示するためのロジック */
  private async fetchMessageFromApi(): ReturnType<typeof fetchMessage> {
    try {
      return await fetchMessage({
        userId: this.idfv,
        token: this.token,
        messageId: this.messageId,
        serviceName: 'repro',
      });
    } catch (error) {
      // TODO: ここも jest でのテスト実施時にのみ UnhandledPromiseRejectionWarning が表示されて鬱陶しいので、どうにかする
      // TODO: repro.rails から Sentry 経由でアラートを投げているが、web-sdk 側でなにかしらの処理が必要になったら以下に追記する
      // ref. https://github.com/reproio/repro.rails/pull/11261#discussion_r298871777
      return Promise.reject(error);
    }
  }

  private async fetchRecommendation({
    specId,
    itemNum,
  }: {
    specId: string | null;
    itemNum: number | null;
  }): ReturnType<typeof fetchRecommendations> {
    let userIdentifier = storage.get(USER_ID_STORAGE_KEY);
    const silverEggUidKey = storage.get(SILVER_EGG_UID_STORAGE_KEY);

    if (silverEggUidKey) {
      const silverEggUid = storage.get(silverEggUidKey);
      userIdentifier = silverEggUid || userIdentifier;
    }

    if (userIdentifier === undefined) {
      throw new Error('Failed to extract necessary parameters for recommendation, aborting showing message.');
    }

    if (specId === null || itemNum === null) {
      throw new Error('Abort to fetch recommendation, because required parameters seem to be missing.');
    }

    let productId: string | undefined;
    let categoryId: string | undefined;

    const params = {
      merchantId: this.silverEggSetting.merchantId,
      domain: this.silverEggSetting.domain,
      userId: userIdentifier,
      specId,
      itemNum,
      productId,
      categoryId,
    };

    // SETに送る prod および cat パラメータの値はベータリリースの時点では .__se_prod, .__se_cat の innerText に固定する
    const prodIdElement = document.querySelector<HTMLElement>('.__se_prod');
    if (prodIdElement != null) {
      params.productId = prodIdElement.innerText;
    }

    const catIdElement = document.querySelector<HTMLElement>('.__se_cat');
    if (catIdElement != null) {
      params.categoryId = catIdElement.innerText;
    }

    try {
      const response = await fetchRecommendations(params);

      if (response.items.length !== itemNum) {
        throw new Error(
          `Abort to fetch Recommendation, due to maybe invalid response. expected item_num: ${itemNum}, but actual: ${response.items.length}`
        );
      }

      return response;
    } catch (error: any) {
      ErrorNotifier.notify({
        error,
        extraPayload: { requestParams: params },
        rollbarOptions: {
          // リクエストのパラメータごとに Rollarで item が生成されるのを避けるため custom fingerprint でグルーピングする
          fingerprint: 'fetchRecommendation-catch-error',
        },
        samplingRatio: 10000,
      });
      throw error;
    }
  }

  // ここから下のメソッドは技術的詳細の塊なので、将来的に BaseMessageDOM のような別オブジェクトに切り出せるかもしれない
  private attachRecommendedMessageEventListener(): void {
    if (this.dom.el == null) return;
    const recommendedItemElements = this.dom.el.querySelectorAll<HTMLAnchorElement>('a.repro--silver-egg--item');
    recommendedItemElements.forEach((el: HTMLAnchorElement) => {
      el.addEventListener('click', (event) => this.handleClickRecommendedMessageLink(event));
    });
  }

  private handleClickRecommendedMessageLink(event: MouseEvent): Promise<void> {
    if (event.currentTarget === null) {
      Promise.reject('Target element is missing.');
    }

    const silverEggUidKey = storage.get(SILVER_EGG_UID_STORAGE_KEY);
    const userIdentifier = (silverEggUidKey && storage.get(silverEggUidKey)) || this.idfv;
    if (userIdentifier === undefined) {
      Promise.reject(`Unable to extract ${USER_ID_STORAGE_KEY} from cookie storage`);
    }

    const crefElement = event.currentTarget as HTMLElement;
    const requestId = crefElement !== null ? crefElement.getAttribute('data-silver-egg-cref') : null;

    const prodElement = event.currentTarget as HTMLElement;
    const productId = prodElement !== null ? prodElement.getAttribute('data-silver-egg-prod') : null;

    return callPostback({
      userId: userIdentifier,
      serviceParams: {
        requestId,
        productId,
      },
      token: this.token,
      messageId: this.messageId,
      serviceName: 'silver_egg',
    }).catch((error) => {
      console.warn(error);
    });
  }
  /* Message API を叩いてメッセージを表示するためのロジックここまで */
}
